import { css } from "styled-system/css";
import { ed, svgText, clip } from "./tokens";
import { useTooltip } from "./useTooltip";

export interface StackKey {
  key: string;
  label: string;
  color: string;
}

export interface StackRow {
  id: string;
  label: string;
  values: Record<string, number>;
}

interface StackedBarsProps {
  rows: StackRow[];
  keys: StackKey[];
  /** Bars as share of each row (100%) or as counts on a shared scale. */
  mode?: "share" | "count";
  ariaLabel: string;
}

const W = 720;
const ROW = 19;
const M = { t: 26, r: 40, b: 8, l: 150 };

/**
 * Horizontal stacked bars. Segments are labeled directly above the first row
 * where they're wide enough; only the keys that didn't fit get a key line.
 */
export function StackedBars({ rows, keys, mode = "share", ariaLabel }: StackedBarsProps) {
  const { bind, node } = useTooltip();
  const iw = W - M.l - M.r;
  const totals = rows.map((r) => keys.reduce((s, k) => s + (r.values[k.key] ?? 0), 0));
  const maxTotal = Math.max(1, ...totals);
  const H = M.t + rows.length * ROW + M.b;

  const layout = rows.map((r, i) => {
    const total = totals[i] ?? 0;
    const scale = mode === "share" ? (total > 0 ? iw / total : 0) : iw / maxTotal;
    let acc = M.l;
    return keys
      .map((k) => {
        const v = r.values[k.key] ?? 0;
        const w = v * scale;
        const seg = { key: k, value: v, x: acc, w, share: total > 0 ? v / total : 0 };
        acc += w;
        return seg;
      })
      .filter((s) => s.value > 0);
  });

  // Direct labels: the first row's segments that have room for their name.
  const labeled = new Set<string>();
  const firstLabels = (layout[0] ?? [])
    .filter((s) => s.w >= s.key.label.length * 6.2 + 6)
    .map((s) => {
      labeled.add(s.key.key);
      return s;
    });
  const unlabeled = keys.filter(
    (k) => !labeled.has(k.key) && rows.some((r) => (r.values[k.key] ?? 0) > 0),
  );

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W }} role="img" aria-label={ariaLabel}>
        {firstLabels.map((s) => (
          <text
            key={s.key.key}
            x={s.x + 2}
            y={M.t - 8}
            style={{ ...svgText.label, fontSize: 11, fill: ed.ink }}
          >
            {s.key.label}
          </text>
        ))}
        {rows.map((r, i) => {
          const y = M.t + i * ROW;
          return (
            <g key={r.id}>
              <text x={M.l - 8} y={y + ROW / 2 + 4} textAnchor="end" style={svgText.label}>
                {clip(r.label, 22)}
              </text>
              {(layout[i] ?? []).map((s) => (
                <rect
                  key={s.key.key}
                  x={s.x}
                  y={y + 3}
                  width={Math.max(0, s.w - 1)}
                  height={ROW - 6}
                  fill={s.key.color}
                  {...bind(
                    <>
                      <b>
                        {r.label} · {s.key.label}
                      </b>
                      <span className="num">{s.value}</span> of{" "}
                      <span className="num">{totals[i]}</span> (
                      <span className="num">{Math.round(s.share * 100)}%</span>)
                    </>,
                  )}
                />
              ))}
              <text
                x={mode === "share" ? W - M.r + 6 : M.l + ((totals[i] ?? 0) / maxTotal) * iw + 6}
                y={y + ROW / 2 + 4}
                style={svgText.num}
              >
                {totals[i]}
              </text>
            </g>
          );
        })}
      </svg>
      {unlabeled.length > 0 && (
        <div className={keyLine}>
          {unlabeled.map((k) => (
            <span key={k.key}>
              <i style={{ background: k.color }} />
              {k.label}
            </span>
          ))}
        </div>
      )}
      {node}
    </>
  );
}

const keyLine = css({
  display: "flex",
  flexWrap: "wrap",
  gap: "4px 14px",
  mt: "4px",
  fontFamily: "sans",
  fontSize: "11.5px",
  color: "ed.ink2",
  "& i": {
    display: "inline-block",
    width: "9px",
    height: "9px",
    mr: "5px",
    verticalAlign: "-1px",
  },
});
